import React, { useMemo, useState } from "react";
import BauhausService from "../services/bauhaus-service";
import {
  EuiButtonIcon,
  EuiToolTip,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiHealth,
  EuiSpacer,
  EuiCallOut,
  EuiLoadingSpinner,
  EuiPanel,
  EuiTitle,
  EuiHorizontalRule,
  EuiCodeBlock,
  EuiIcon,
  EuiBasicTable,
  EuiBadge,
  EuiStat,
    EuiTabs,
  EuiTab,
  EuiCopy,
  EuiButtonEmpty,

} from "@elastic/eui";

/** Keys base */
const BAUHAUS_KEYS = [
  "saludoInicial",
  "identificacionCliente",
  "transferenciaYGestionEspera",
  "cualificacionFichaCliente",
  "mensajeDespedida",
  "finalizacionInteraccion",
  "lenguajeComprensible",
  "vocabularioPositivoVariado",
  "claridadBrevedadMensajes",
  "velocidadExposicion",
  "seguridadArgumentacion",
  "escuchaActiva",
  "amabilidadCortesia",
  "formaDirigirseCliente",
  "empatia",
  "identificacionMotivo",
  "historicoCliente",
  "conocimientoProductosServicios",
  "creacionIncidenciaConsulta",
  "informacionSiguientesPasos",
];


// Pesos del estándar (en fracción 0..1)
const WEIGHTS = {
  // 1. Protocolos (10%)
  saludoInicial: 0.0167,
  identificacionCliente: 0.0167,
  transferenciaYGestionEspera: 0.0167,
  cualificacionFichaCliente: 0.0167,
  mensajeDespedida: 0.0167,
  finalizacionInteraccion: 0.0167,

  // 2. Recursos (10%)
  lenguajeComprensible: 0.02,
  vocabularioPositivoVariado: 0.02,
  claridadBrevedadMensajes: 0.02,
  velocidadExposicion: 0.02,
  seguridadArgumentacion: 0.02,

  // 3. Trato (10%)
  escuchaActiva: 0.025,
  amabilidadCortesia: 0.025,
  formaDirigirseCliente: 0.025,
  empatia: 0.025,

  // 4. Motivo (20%)
  identificacionMotivo: 0.1,
  historicoCliente: 0.1,

  // 5. Argumentación/Solución (25%)
  conocimientoProductosServicios: 0.25,

  // 6. Cierre y registro (25%)
  creacionIncidenciaConsulta: 0.125,
  informacionSiguientesPasos: 0.125,
};


const CRITICAL_KEYS = new Set([
  "transferenciaYGestionEspera",
  "finalizacionInteraccion",
  "escuchaActiva",
  "identificacionMotivo",
  "historicoCliente",
  "creacionIncidenciaConsulta",
]);

/** Etiquetas UI */
const LABELS = {
  valoracionAgente: "Valoración del agente",
  sentimientoCliente: "Sentimiento del cliente",
  etiquetaLlamada: "Etiqueta de la llamada",
  score100: "Score 008Q (0-100)",

  saludoInicial: "1.1 Saludo inicial",
  identificacionCliente: "1.2 Identificación del cliente",
  transferenciaYGestionEspera: "1.3 Transferencias y gestión de esperas (Crítico)",
  cualificacionFichaCliente: "1.4 Cualificación de ficha de cliente",
  mensajeDespedida: "1.5 Uso de mensaje de despedida",
  finalizacionInteraccion: "1.6 Finalización de la interacción (Crítico)",

  lenguajeComprensible: "2.1 Uso de lenguaje comprensible",
  vocabularioPositivoVariado: "2.2 Vocabulario positivo y variado",
  claridadBrevedadMensajes: "2.3 Claridad y brevedad en los mensajes",
  velocidadExposicion: "2.4 Velocidad de exposición",
  seguridadArgumentacion: "2.5 Seguridad en la argumentación",

  escuchaActiva: "3.1 Escucha activa (Crítico)",
  amabilidadCortesia: "3.2 Amabilidad y cortesía",
  formaDirigirseCliente: "3.3 Forma de dirigirse al cliente",
  empatia: "3.4 Empatía",

  identificacionMotivo: "4.1 Identificación de motivo (Crítico)",
  historicoCliente: "4.2 Histórico del cliente con Bauhaus (Crítico)",

  conocimientoProductosServicios: "5.1 Seguridad en la argumentación y resolución de problemas",

  creacionIncidenciaConsulta: "6.1 Creación de la incidencia/consulta (Crítico)",
  informacionSiguientesPasos: "6.2 Información de siguientes pasos",


  summarization: "Resumen de la llamada",
  topics: "Temas",
  agentEmail: "Agente (email)",
  queue: "Queue",
};

const SECTIONS = [
  { title: "KPIs", percent: null, keys: ["valoracionAgente", "sentimientoCliente", "etiquetaLlamada", "agentEmail", "queue"] },
  {
    title: "1. Protocolos de atención",
    percent: "10%",
    keys: [
      "saludoInicial",
      "identificacionCliente",
      "transferenciaYGestionEspera",
      "cualificacionFichaCliente",
      "mensajeDespedida",
      "finalizacionInteraccion",
    ],
  },
  {
    title: "2. Recursos comunicativos",
    percent: "10%",
    keys: [
      "lenguajeComprensible",
      "vocabularioPositivoVariado",
      "claridadBrevedadMensajes",
      "velocidadExposicion",
      "seguridadArgumentacion",
    ],
  },
  {
    title: "3. Trato al cliente",
    percent: "10%",
    keys: ["escuchaActiva", "amabilidadCortesia", "formaDirigirseCliente", "empatia"],
  },
  { title: "4. Motivo de la interacción", percent: "20%", keys: ["identificacionMotivo", "historicoCliente"] },
  { title: "5. Argumentación / solución", percent: "25%", keys: ["conocimientoProductosServicios"] },
  {
    title: "6. Cierre y registro en sistemas",
    percent: "25%",
    keys: ["creacionIncidenciaConsulta", "informacionSiguientesPasos"],
  },
];

const STATUS_COLOR = (val) => {
  switch (String(val || "").toUpperCase()) {
    case "SI_CUMPLE":
      return "success";
    case "NO_CUMPLE":
      return "danger";
    case "NO_APLICA":
      return "warning";
    default:
      return "subdued";
  }
};

function formatConfidence(x) {
  if (x === undefined || x === null || x === "") return null;
  const n = Number(x);
  if (Number.isNaN(n)) return null;
  if (n >= 0 && n <= 1) return `${Math.round(n * 100)}%`;
  if (n > 1 && n <= 100) return `${Math.round(n)}%`;
  return `${n}`;
}

function formatWeight(key) {
  const w = WEIGHTS[key];
  if (!w) return null;
  return `${(w * 100).toFixed(2)}%`;
}

/** Lee valor desde plano o desde estandarBauhausResultado */
function getEvalValue(payload, key) {
  if (!payload) return undefined;
  if (payload[key] != null) return payload[key];
  return payload?.estandarBauhausResultado?.[key];
}

/** Lee detalle desde flattened o desde objetos */
function getDetail(payload, key) {
  const flat = payload?.estandarBauhausDetalle;
  const confFromFlat = flat?.[`${key}.confianza`];
  const justFromFlat = flat?.[`${key}.justificacion`];

  const confFromObj = payload?.estandarBauhausConfianza?.[key];
  const justFromObj = payload?.estandarBauhausJustificacion?.[key];

  const conf = confFromFlat ?? confFromObj;
  const just = justFromFlat ?? justFromObj;

  return { conf, just };
}

/** Score /100 ponderado (fallback si no existe payload.quality.score100) */
function computeScore100Fallback(payload) {
  let num = 0;
  let den = 0;

  const failedCritical = Array.from(CRITICAL_KEYS).some((k) => {
    const v = String(getEvalValue(payload, k) || "").toUpperCase();
    return v === "NO_CUMPLE";
  });

  if (failedCritical) return 0;

  for (const k of BAUHAUS_KEYS) {
    const w = WEIGHTS[k] ?? 0;
    const v = String(getEvalValue(payload, k) || "").toUpperCase();

    if (v === "NO_APLICA" || v === "" || v === "NO_DETECTADO") continue;

    if (v === "SI_CUMPLE" || v === "NO_CUMPLE") {
      den += w;
      if (v === "SI_CUMPLE") num += w;
    }
  }

  if (den <= 0) return null;
  return Math.round((num / den) * 100);
}

function TooltipContent({ criterioKey, payload }) {
  const { conf, just } = getDetail(payload, criterioKey);
  const confFmt = formatConfidence(conf);

  // breakdown (si viene del nuevo elastic.js)
  const b = payload?.quality?.breakdown?.[criterioKey];
  const weightStr = formatWeight(criterioKey);
  const earnedStr =
    b && typeof b.earned === "number" ? `${(b.earned).toFixed(2)}%` : null; // OJO: en tu ES lo guardas en "peso en %", si lo cambias, ajusta
  const applicableStr = b?.applicable === false ? "NO_APLICA" : b?.applicable === true ? "APLICA" : null;

  return (
    <EuiText size="xs">
      <strong>{LABELS[criterioKey] || criterioKey}</strong>

      {CRITICAL_KEYS.has(criterioKey) && (
        <>
          <br />
          <EuiBadge color="danger">Crítico</EuiBadge>
        </>
      )}

      {weightStr && (
        <>
          <br />
          <strong>Peso:</strong> {weightStr}
        </>
      )}

      {applicableStr && (
        <>
          <br />
          <strong>Aplicación:</strong> {applicableStr}
        </>
      )}

      {earnedStr && (
        <>
          <br />
          <strong>Aporta:</strong> {earnedStr}
        </>
      )}

      {confFmt && (
        <>
          <br />
          <strong>Confianza:</strong> {confFmt}
        </>
      )}
      {just && (
        <>
          <br />
          <strong>Justificación:</strong> {just}
        </>
      )}

      {!confFmt && !just && !weightStr && !earnedStr && (
        <>
          <br />
          Sin detalle guardado para este criterio.
        </>
      )}
    </EuiText>
  );
}

function formatConversationText(conversationTurns = []) {
  return (conversationTurns || [])
    .map((t) => `Speaker ${t?.speaker ?? "?"}: ${t?.text ?? ""}`.trim())
    .join("\n");
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadJson(filename, obj) {
  const text = JSON.stringify(obj, null, 2);
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


export default function Bauhaus008q({ callId, hasRecording, companyName, iconSrc, token }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [payload, setPayload] = useState(null);
  const [convTab, setConvTab] = useState("chat"); // "chat" | "text" | "json"


  const normalizedCN = (companyName || localStorage.getItem("CN") || "")
    .toString()
    .trim()
    .toLowerCase();

  const canShow = normalizedCN === "bauhaus españa" && Boolean(hasRecording) && Boolean(callId);
  const authToken = token || sessionStorage.getItem("krakenT") || "";

  const fetchData = async () => {
    setLoading(true);
    setLoadError("");
    setPayload(null);
    try {
      const res = await BauhausService.call_by_id({ token: authToken, callId });
      setPayload(res.data?.data || null);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "No se pudo obtener la información de la llamada";
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  const onOpen = async () => {
    setOpen(true);
    await fetchData();
  };

  const onClose = () => {
    setOpen(false);
    setLoadError("");
    setPayload(null);
  };

  const normalizedPayload = useMemo(() => {
    if (!payload) return null;
    const etiqueta = payload?.ticket?.etiquetaLlamada ?? payload?.etiquetaLlamada ?? "Otros";
    return { ...payload, etiquetaLlamada: etiqueta };
  }, [payload]);

  // ✅ Fuente de verdad: quality.score100 (fallback a compute local)
  const score100 = useMemo(() => {
    const q = normalizedPayload?.quality;
    if (q && typeof q.score100 === "number") return Math.round(q.score100);
    return computeScore100Fallback(normalizedPayload);
  }, [normalizedPayload]);

  const failedCriticalKeys = useMemo(() => {
  const fromQuality = normalizedPayload?.quality?.failedCriticalKeys;
  if (Array.isArray(fromQuality)) return fromQuality;

  return Array.from(CRITICAL_KEYS).filter((k) => {
    const v = String(getEvalValue(normalizedPayload, k) || "").toUpperCase();
    return v === "NO_CUMPLE";
  });
}, [normalizedPayload]);

const criticalFail = failedCriticalKeys.length > 0;
  const buildRows = (keys, data) =>
    keys.map((k) => {
      const value = k === "etiquetaLlamada" ? data?.etiquetaLlamada : getEvalValue(data, k);

      if (k === "valoracionAgente") {
        return { id: k, criterioKey: k, criterio: LABELS[k] || k, estado: value != null ? `${value}` : "-", dotColor: value != null ? "primary" : "subdued", tooltip: false };
      }
      if (k === "sentimientoCliente") {
        const color = value === "positive" ? "success" : value === "negative" ? "danger" : "subdued";
        return { id: k, criterioKey: k, criterio: LABELS[k] || k, estado: value || "-", dotColor: color, tooltip: false };
      }
      if (k === "etiquetaLlamada") {
        return { id: k, criterioKey: k, criterio: LABELS[k] || k, estado: value || "-", dotColor: value && value !== "-" ? "accent" : "subdued", tooltip: false };
      }
      if (k === "agentEmail") {
        return { id: k, criterioKey: k, criterio: LABELS[k] || k, estado: value || "-", dotColor: value ? "primary" : "subdued", tooltip: false };
      }
      if (k === "queue") {
        return { id: k, criterioKey: k, criterio: LABELS[k] || k, estado: value || "-", dotColor: value ? "primary" : "subdued", tooltip: false };
      }

      return {
        id: k,
        criterioKey: k,
        criterio: LABELS[k] || k,
        estado: value ?? "NO_DETECTADO",
        dotColor: STATUS_COLOR(value),
        tooltip: true,
      };
    });

  const columns = [
    {
      field: "criterio",
      name: "Criterio",
      truncateText: true,
      width: "70%",
      render: (txt, item) => (
        <EuiFlexGroup gutterSize="s" alignItems="center">
          <EuiFlexItem grow={false}>
            {CRITICAL_KEYS.has(item.criterioKey) ? <EuiBadge color="danger">Crítico</EuiBadge> : null}
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">{txt}</EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      field: "estado",
      name: "Estado",
      align: "right",
      width: "30%",
      render: (estado, item) => {
        const cell = (
          <EuiFlexGroup gutterSize="s" alignItems="center" justifyContent="flexEnd">
            <EuiFlexItem grow={false}>
              <EuiHealth color={item.dotColor} />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiText size="s">{estado}</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        );

        if (item.tooltip) {
          return (
            <EuiToolTip
              position="top"
              display="block"
              content={<TooltipContent criterioKey={item.criterioKey} payload={normalizedPayload} />}
            >
              <span style={{ cursor: "help", display: "inline-block" }}>{cell}</span>
            </EuiToolTip>
          );
        }
        return cell;
      },
    },
  ];

  if (!canShow) return null;

  return (
    <>
      <EuiToolTip content="Ver evaluación de calidad (Bauhaus 008Q)">
        <EuiButtonIcon aria-label="Bauhaus 008Q" display="base" onClick={onOpen} iconType={iconSrc ? iconSrc : "inspect"} />
      </EuiToolTip>

      {open && (
        <EuiModal onClose={onClose} style={{ width: 920 }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <EuiFlexGroup gutterSize="s" alignItems="center">
                <EuiFlexItem grow={false}>
                  {iconSrc ? (
                    <img src={iconSrc} alt="008Q" style={{ width: 32, height: 32, display: "block" }} />
                  ) : (
                    <EuiIcon type="inspect" />
                  )}
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <span>Bauhaus 008Q — Evaluación de calidad</span>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiModalHeaderTitle>
          </EuiModalHeader>

          <EuiModalBody>
            {loading && (
              <EuiFlexGroup justifyContent="center" alignItems="center">
                <EuiLoadingSpinner size="xl" />
              </EuiFlexGroup>
            )}

            {!loading && loadError && (
              <EuiCallOut title="No se pudo cargar la información" color="danger" iconType="alert">
                <p>{loadError}</p>
              </EuiCallOut>
            )}

            {!loading && !loadError && normalizedPayload && (
              <>
                {/* Score + críticos */}
                <EuiPanel paddingSize="m">
                  <EuiFlexGroup alignItems="center" gutterSize="m" wrap justifyContent="between">
                    <EuiFlexItem grow={false}>
                      <EuiStat
                        title={score100 == null ? "—" : `${score100}/100`}
                        description={LABELS.score100}
                        titleColor={
                          score100 == null
                            ? "subdued"
                            : score100 >= 85
                            ? "success"
                            : score100 >= 70
                            ? "warning"
                            : "danger"
                        }
                      />
                    </EuiFlexItem>

                    <EuiFlexItem>
                      <EuiText size="xs" color="subdued">
                        NO_APLICA no penaliza (se excluye del denominador).
                      </EuiText>

                      {criticalFail && (
                        <>
                          <EuiSpacer size="s" />
                          <EuiCallOut
                            title="Fallo en ítems críticos"
                            color="danger"
                            iconType="alert"
                            size="s"
                          >
                            {failedCriticalKeys?.length ? (
                              <EuiFlexGroup gutterSize="s" wrap>
                                {failedCriticalKeys.map((k) => (
                                  <EuiFlexItem key={k} grow={false}>
                                    <EuiBadge color="danger">{LABELS[k] || k}</EuiBadge>
                                  </EuiFlexItem>
                                ))}
                              </EuiFlexGroup>
                            ) : (
                              <p>Se marcó criticalFail pero no hay lista de críticos fallados.</p>
                            )}
                          </EuiCallOut>
                        </>
                      )}
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiPanel>

                <EuiSpacer size="m" />

                {/* Resumen y temas */}
                {(normalizedPayload.summarization ||
                  (normalizedPayload.topics && normalizedPayload.topics.length)) && (
                  <EuiPanel paddingSize="m">
                    <EuiTitle size="xs">
                      <h3>Resumen</h3>
                    </EuiTitle>
                    <EuiSpacer size="s" />
                    {normalizedPayload.summarization && (
                      <>
                        <EuiText size="s">{normalizedPayload.summarization}</EuiText>
                        <EuiSpacer size="m" />
                      </>
                    )}
                    {normalizedPayload.topics && normalizedPayload.topics.length > 0 && (
                      <>
                        <EuiText size="s">
                          <strong>{LABELS.topics}:</strong>
                        </EuiText>
                        <EuiSpacer size="xs" />
                        <EuiFlexGroup gutterSize="s" wrap>
                          {normalizedPayload.topics.map((t, idx) => (
                            <EuiFlexItem key={`${t}-${idx}`} grow={false}>
                              <EuiBadge color="hollow">{t}</EuiBadge>
                            </EuiFlexItem>
                          ))}
                        </EuiFlexGroup>
                      </>
                    )}
                  </EuiPanel>
                )}

                <EuiSpacer size="m" />
                <EuiSpacer size="m" />

{Array.isArray(normalizedPayload?.conversationTurns) &&
  normalizedPayload.conversationTurns.length > 0 && (() => {
    const turns = normalizedPayload.conversationTurns;
    const convText = formatConversationText(turns);

    const baseName = `conversation_${callId || normalizedPayload.call_id || "call"}`;
    const fileTxt = `${baseName}.txt`;
    const fileJson = `${baseName}.json`;

    return (
      <EuiPanel paddingSize="m">
        <EuiFlexGroup alignItems="center" justifyContent="spaceBetween" gutterSize="m">
          <EuiFlexItem grow={false}>
            <EuiTitle size="xs">
              <h3>Conversación</h3>
            </EuiTitle>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiFlexGroup gutterSize="s" alignItems="center">
              <EuiFlexItem grow={false}>
                <EuiCopy textToCopy={convText}>
                  {(copy) => (
                    <EuiButtonEmpty size="s" iconType="copyClipboard" onClick={copy}>
                      Copiar
                    </EuiButtonEmpty>
                  )}
                </EuiCopy>
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiButton
                  size="s"
                  iconType="download"
                  onClick={() => downloadText(fileTxt, convText)}
                >
                  Descargar TXT
                </EuiButton>
              </EuiFlexItem>

              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  size="s"
                  iconType="download"
                  onClick={() => downloadJson(fileJson, turns)}
                >
                  JSON
                </EuiButtonEmpty>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="s" />

        <EuiTabs size="s">
          <EuiTab isSelected={convTab === "chat"} onClick={() => setConvTab("chat")}>
            Vista chat
          </EuiTab>
          <EuiTab isSelected={convTab === "text"} onClick={() => setConvTab("text")}>
            Texto
          </EuiTab>
          <EuiTab isSelected={convTab === "json"} onClick={() => setConvTab("json")}>
            JSON
          </EuiTab>
        </EuiTabs>

        <EuiSpacer size="m" />

        {convTab === "chat" && (
          <EuiFlexGroup direction="column" gutterSize="s">
            {turns.map((t, idx) => {
              const speaker = t?.speaker ?? "?";
              const isEven = typeof speaker === "number" ? speaker % 2 === 0 : false; // simple, sin asumir roles
              return (
                <EuiFlexItem key={idx}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: isEven ? "flex-start" : "flex-end",
                    }}
                  >
                    <div
                      style={{
                        maxWidth: "78%",
                        padding: "10px 12px",
                        borderRadius: 14,
                        border: "1px solid #D3DAE6",
                        background: isEven ? "#F5F7FA" : "#FFF",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      }}
                    >
                      <EuiText size="xs" color="subdued">
                        <strong>Speaker {speaker}</strong>
                      </EuiText>
                      <EuiText size="s" style={{ marginTop: 4 }}>
                        {t?.text || ""}
                      </EuiText>
                    </div>
                  </div>
                </EuiFlexItem>
              );
            })}
          </EuiFlexGroup>
        )}

        {convTab === "text" && (
          <EuiCodeBlock language="text" isCopyable overflowHeight={260} fontSize="s">
            {convText}
          </EuiCodeBlock>
        )}

        {convTab === "json" && (
          <EuiCodeBlock language="json" isCopyable overflowHeight={260} fontSize="s">
            {JSON.stringify(turns, null, 2)}
          </EuiCodeBlock>
        )}
      </EuiPanel>
    );
  })()}


                {/* Tablas */}
                {SECTIONS.map((section) => {
                  const rows = buildRows(section.keys, normalizedPayload);
                  const hasData = rows.some((r) => r.estado && r.estado !== "NO_DETECTADO" && r.estado !== "-");

                  return (
                    <EuiPanel key={section.title} paddingSize="m" style={{ marginBottom: 12 }}>
                      <EuiTitle size="xs">
                        <h3>
                          {section.title}
                          {section.percent ? ` · ${section.percent}` : ""}
                        </h3>
                      </EuiTitle>
                      <EuiSpacer size="s" />
                      <EuiBasicTable items={rows} columns={columns} tableCaption={section.title} compressed />
                      {!hasData && (
                        <>
                          <EuiSpacer size="s" />
                          <EuiText size="xs" color="subdued">
                            No se detectaron valores para esta sección.
                          </EuiText>
                        </>
                      )}
                    </EuiPanel>
                  );
                })}

                <EuiSpacer size="m" />
                <EuiHorizontalRule margin="m" />
                <EuiTitle size="xxs">
                  <h4>_source (JSON)</h4>
                </EuiTitle>
                <EuiSpacer size="s" />
                <EuiCodeBlock language="json" isCopyable overflowHeight={280} fontSize="s" lineNumbers>
                  {JSON.stringify(normalizedPayload, null, 2)}
                </EuiCodeBlock>
              </>
            )}
          </EuiModalBody>

          <EuiModalFooter>
            <EuiButton onClick={onClose} fill>
              Cerrar
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}
    </>
  );
}
